import { Mail, MapPin, NotebookPen, Phone, ReceiptText, ShoppingBag } from "lucide-react";
import EmptyState from "../components/EmptyState";
import { dateText, money } from "./orderStatusMeta";

export function OrderItemsPanel({ order }) {
  return (
    <section className="order-panel">
      <header><h2>Items</h2></header>
      <div className="order-item-list">
        {order.items?.length ? order.items.map((item) => (
          <article className="order-line-item" key={item.id}>
            <div>
              <strong>{item.quantity} x {item.menu_item_name_snapshot}</strong>
              <small>{item.category_name_snapshot || "Menu item"}</small>
              {item.special_instructions && <p>{item.special_instructions}</p>}
              <div className="order-line-extras">
                {item.flavors?.map((flavor) => <span key={flavor.id}>Flavor: {flavor.flavor_name_snapshot}</span>)}
                {item.add_ons?.map((addOn) => <span key={addOn.id}>Add-on: {addOn.add_on_name_snapshot}</span>)}
              </div>
            </div>
            <strong>{money(item.line_total)}</strong>
          </article>
        )) : <EmptyState icon={ShoppingBag} title="No item snapshots" description="Line items will appear once checkout creates orders." />}
      </div>
    </section>
  );
}

export function OrderPricingPanel({ order }) {
  const rows = [
    ["Subtotal", order.subtotal],
    ["Discount", `-${money(order.discount_amount)}`],
    ["Delivery fee", order.delivery_fee],
    ["Tax", order.tax_amount],
    ["Paid", `-${money(order.amount_paid)}`],
    ["Refunded", order.amount_refunded],
  ];
  return (
    <section className="order-panel">
      <header><h2>Pricing</h2></header>
      <dl className="order-price-list">
        {rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{typeof value === "string" ? value : money(value)}</dd></div>)}
        <div className="order-price-list__total"><dt>Total</dt><dd>{money(order.total_amount)}</dd></div>
        <div className="order-price-list__balance"><dt>Balance due</dt><dd>{money(order.balance_due)}</dd></div>
      </dl>
      {order.coupon_code_snapshot && <p className="order-muted">Coupon: {order.coupon_code_snapshot}</p>}
      {(order.stripe_checkout_session_id || order.stripe_payment_intent_id || order.stripe_invoice_id) && <p className="order-muted">Stripe refs are stored for reconciliation.</p>}
    </section>
  );
}

export function OrderCustomerPanel({ order }) {
  return (
    <section className="order-panel">
      <header><h2>Customer</h2></header>
      <div className="order-contact-card">
        <strong>{order.customer_name_snapshot || "Guest customer"}</strong>
        <span><Mail size={15} /> {order.customer_email_snapshot || "No email"}</span>
        <span><Phone size={15} /> {order.customer_phone_snapshot || "No phone"}</span>
      </div>
      {order.customer_notes && <p className="order-note-block">{order.customer_notes}</p>}
      {order.is_gift && <p className="order-note-block">Gift message: {order.gift_message || "No gift message"}</p>}
    </section>
  );
}

export function OrderDeliveryPanel({ order, scheduleAt, setScheduleAt, requestedDate, setRequestedDate, requestedTime, setRequestedTime, isSaving, onSchedule, onUpdateRequested }) {
  return (
    <section className="order-panel">
      <header><h2>Delivery</h2></header>
      <div className="order-contact-card">
        <span><MapPin size={15} /> {order.delivery_address_line_1_snapshot || "No address"}</span>
        {order.delivery_address_line_2_snapshot && <span>{order.delivery_address_line_2_snapshot}</span>}
        <span>{[order.delivery_city_snapshot, order.delivery_state_snapshot, order.delivery_postal_code_snapshot].filter(Boolean).join(", ") || "No city snapshot"}</span>
        <span><strong>Requested Delivery:</strong> {order.requested_delivery_date || "Not provided"} {order.requested_delivery_window || ""}</span>
        <span><strong>Delivery Status:</strong> {order.delivery_status_label || order.delivery_status || "Not set"}</span>
        <span>Scheduled: {order.scheduled_delivery_at ? dateText(order.scheduled_delivery_at) : "Not scheduled"}</span>
      </div>
      {order.delivery_instructions_snapshot && <p className="order-note-block">{order.delivery_instructions_snapshot}</p>}
      <form className="order-inline-form" onSubmit={(event) => { event.preventDefault(); onUpdateRequested(requestedDate, requestedTime); }}>
        <label>Requested delivery date<input type="date" value={requestedDate} onChange={(event) => setRequestedDate(event.target.value)} /></label>
        <label>Requested delivery time<input type="time" value={requestedTime} onChange={(event) => setRequestedTime(event.target.value)} /></label>
        <button type="submit" className="btn btn--secondary" disabled={isSaving || !requestedDate || !requestedTime}>Update requested delivery</button>
      </form>
      <form className="order-inline-form" onSubmit={(event) => { event.preventDefault(); onSchedule(scheduleAt); }}>
        <label>Schedule delivery<input type="datetime-local" value={scheduleAt} onChange={(event) => setScheduleAt(event.target.value)} /></label>
        <button type="submit" className="btn btn--secondary" disabled={isSaving || !scheduleAt}>Schedule</button>
      </form>
    </section>
  );
}

export function OrderNotesPanel({ order, note, setNote, isSaving, onAddNote }) {
  return (
    <section className="order-panel">
      <header><h2>Internal Notes</h2></header>
      <form className="order-note-form" onSubmit={(event) => { event.preventDefault(); onAddNote(note); }}>
        <textarea rows="3" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Add an internal order note" />
        <button type="submit" className="btn" disabled={isSaving || !note.trim()}>Add note</button>
      </form>
      <div className="order-note-list">
        {order.notes?.length ? order.notes.map((row) => (
          <article key={row.id}>
            <p>{row.note}</p>
            <small>{row.created_by_name || "SugaLumps"} - {dateText(row.created_at)}</small>
          </article>
        )) : <EmptyState icon={NotebookPen} title="No notes" description="Internal notes stay attached to the order audit trail." />}
      </div>
    </section>
  );
}

export function OrderActivityPanel({ order }) {
  return (
    <section className="order-panel">
      <header><h2>Activity</h2></header>
      <div className="order-activity-list">
        {order.activities?.length ? order.activities.map((activity) => (
          <article key={activity.id}>
            <span><ReceiptText size={15} /></span>
            <div><strong>{activity.title}</strong><p>{activity.description || activity.activity_type_label}</p><small>{dateText(activity.created_at)}</small></div>
          </article>
        )) : <p className="order-muted">No order activity yet.</p>}
      </div>
    </section>
  );
}
